import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import useMedia from "use-media";
import { isIOS, isMacOs, isSafari, isMobile } from "react-device-detect";
import { useSelector } from "react-redux";

import {
  Control,
  Button,
  Filters,
  Form,
  InputWrapper,
  Query,
  Wrapper,
  Results,
  Result,
  LoadingWrapper,
  LoadingSpinner,
  Settings,
  SettingsButton,
  DesktopSettingsWrapper,
  WrapperSettings,
  SmallSettings,
  LabelGeolocalizacao,
  InputButton,
  InputClear,
  LocationAlert,
  AlertWrapper,
  MessageLocationAlert,
  AlertLoadingBar
} from "../../../../components/MapaOndeUsar/styles";
import { TextWrap, Title, Text, Sorriso } from "./style";

import Icon from "../../../../components/Icons";
import { getAuth, fetchData } from "../../../../utils/Mapa/query";
import GoogleMaps from "../../../../components/MapaOndeUsar/";
import RenderResults from "../../../../utils/Mapa/renderResults.js";
import HistoricoBusca from "../../../../components/MapaOndeUsar/HistoricoBusca";

const selector = (state) => ({
  component: state.component
});

export default function Mapa() {
  const mapRef = useRef();
  const [latlong, setLatlong] = useState();
  const [queryResults, setQueryResults] = useState(null);
  const [queryTerm, setQueryTerm] = useState("");
  const [queryFilters, setQueryFilters] = useState([]);
  const [idle, setIdle] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [searchHistory, setSearchHistory] = useState(null);
  const [firstSearch, setFirstSearch] = useState(true);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [geolocationSetting, setGeolocationSetting] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  const [showLocationAlert, setShowLocationAlert] = useState(false);
  const [categories, setCategories] = useState([
    {
      category: "CONCESSIONARIA",
      active: false,
      displayName: "Pedágio"
    },
    {
      category: "ESTACIONAMENTO",
      active: false,
      displayName: "Estacionamento"
    }
  ]);

  const { component } = useSelector(selector);
  const { mapTitle, mapDescriptions } = component;

  const isMobileDevice = useMedia({ maxWidth: "767px" });

  const loadHistoryFromStorage = () => {
    setSearchHistory(JSON.parse(window.localStorage.getItem("search-history")));
  };

  useEffect(() => {
    loadHistoryFromStorage();
    if (isMobileDevice) setShowSettings(false);
  }, [isMobileDevice]);

  const showHistory = () => {
    setShowSearchHistory(true);
  };

  const hideHistory = () => {
    setTimeout(() => {
      setShowSearchHistory(false);
    }, 300);
  };

  const registerSearchHistory = useCallback(async(query = "", categoryList = []) => {
    const history = searchHistory || [];
    if (history.find(item => item.query === query)) return;
    if (history.length >= 2) history.pop();
    history.unshift({ query, categoryList });
    setSearchHistory(history);
    window.localStorage.setItem("search-history", JSON.stringify(history));
  }, [searchHistory]);

  const removeDuplicatedIds = resultList => {
    return (resultList || []).filter(
      (result, index, a) => a.findIndex(t => t.id === result.id) === index
    );
  };

  const isLocationEqual = (location1, location2) => {
    return (
      location1.latitude === location2.latitude &&
      location1.longitude === location2.longitude
    );
  };

  const removeDuplicatedLocations = useCallback((resultList) => {
    return resultList.filter(
      (result, index, a) =>
        a.findIndex(t => isLocationEqual(t.location, result.location)) === index
    );
  }, []);

  const compareDistance = (locationA, locationB) => {
    const distanceA = parseFloat(locationA.distance);
    const distanceB = parseFloat(locationB.distance);
    if (distanceA < distanceB) return -1;
    if (distanceA > distanceB) return 1;
    return 0;
  };

  useEffect(() => {
    const options = {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 0
    };
    navigator &&
      navigator.geolocation &&
      navigator.geolocation.getCurrentPosition(
        position => {
          setLatlong({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        e => console.log("map error", e.message),
        options
      );
  }, [mapReady]);

  useEffect(() => {
    if (mapReady) {
      setIdle(false);
      if (latlong) mapRef.current.centerMap();
    }
  }, [mapReady, latlong]);

  const queryAPI = useCallback(async (text = "", queryCategories = []) => {
    // reset results
    setQueryResults(null);

    if (!text.length && !queryCategories.length) {
      return;
    }

    // fetch content
    setIdle(true);
    hideHistory();
    let results;
    const key = await getAuth();
    if (key && key.access_token) {
      results = await fetchData(
        key.access_token,
        text,
        latlong || false,
        queryCategories ? queryCategories.join(",") : "",
        geolocationSetting
      );
    }

    setIdle(false);
    const resultsUniqueIdsAndLocations = removeDuplicatedLocations(
      removeDuplicatedIds(results)
    );
    if (latlong) resultsUniqueIdsAndLocations.sort(compareDistance);
    if (text.length) registerSearchHistory(text, queryCategories);
    setQueryResults(resultsUniqueIdsAndLocations);
  }, [geolocationSetting, latlong, registerSearchHistory, removeDuplicatedLocations]);

  const checkFirstSearch = useCallback(() => {
    if (firstSearch) setFirstSearch(false);
  }, [firstSearch]);

  const handleHistoricoClick = (queryText, categoryList) => {
    checkFirstSearch();
    setQueryTerm(queryText);
    const cat = categories.map(item => ({
      ...item,
      active: categoryList.includes(item.category)
    }));
    setCategories(cat);
    queryAPI(queryText, categoryList);
  };

  const handleGeolocationChange = () => {
    setGeolocationSetting(!geolocationSetting);
  };

  useEffect(() => {
    if (mapReady && geolocationSetting) {
      queryAPI(
        queryTerm,
        queryFilters.length
          ? queryFilters
          : categories.map(item => item.category)
      );
    }
    if (mapReady && !geolocationSetting) {
      queryAPI(queryTerm, queryFilters);
    }
  }, [geolocationSetting, mapReady, categories, queryAPI, queryFilters]);

  const onClickClearHistorico = index => {
    setSearchHistory(
      searchHistory.filter((item, itemIndex) => itemIndex !== index)
    );
    window.localStorage.setItem(
      "search-history",
      JSON.stringify(
        searchHistory.filter((item, itemIndex) => itemIndex !== index)
      )
    );

    if (searchHistory.length === 0) {
      setShowSearchHistory(false);
    }
  };

  const historicoDeBusca = () => {
    if (!searchHistory) return null;
    if (showSearchHistory && searchHistory.length) {
      return (
        <HistoricoBusca
          listaHistorico={searchHistory}
          onClickHistorico={handleHistoricoClick}
          onClickClearHistorico={onClickClearHistorico}
        />
      );
    }
    return null;
  };

  const toggleFilter = useCallback((event, val) => {
    event.preventDefault();
    checkFirstSearch();
    if (idle) return;
    let filters;
    if (queryFilters.indexOf(val) === -1) {
      filters = queryFilters.concat([val]);
    } else {
      filters = queryFilters.filter(item => item !== val);
    }
    const cat = categories.map(item => ({
      ...item,
      active: filters.includes(item.category)
    }));
    setCategories(cat);
    setQueryFilters(filters);
  }, [categories, checkFirstSearch, idle, queryFilters]);

  useEffect(() => {
    queryAPI(queryTerm, queryFilters);
  }, [queryFilters, queryAPI]);

  useEffect(() => {
    if (!firstSearch && !latlong) {
      setShowLocationAlert(true);

      const locationAlertTimeout = setTimeout(() => {
        setShowLocationAlert(false);
      }, [8000]);

      return () => clearTimeout(locationAlertTimeout);
    }
  }, [firstSearch, latlong]);

  const onSubmit = async event => {
    event.preventDefault();
    checkFirstSearch();

    if (idle) {
      return;
    }

    queryAPI(queryTerm, queryFilters, latlong);
  };

  const onSelect = coord => {
    if (!coord) return;
    setQueryResults(
      queryResults.map(item => {
        const selected =
          item.location.latitude === coord.latitude &&
          item.location.longitude === coord.longitude;
        return { ...item, selected };
      }, null)
    );
  };

  const handleShowSettingsChange = () => {
    setShowSettings(!showSettings);
  };

  const CenterLocationButton = () => {
    if (latlong) {
      return (
        <SettingsButton onClick={() => mapRef.current.centerMap()}>
          <Icon name="Location" />
        </SettingsButton>
      );
    }
    return null;
  };

  const SettingsBox = () => {
    if (latlong) {
      return (
        <Settings>
          <LabelGeolocalizacao
            htmlFor="ativar-geolocalizacao"
            applyStyle={!isIOS && !isMacOs && !isSafari && !isMobile}
          >
            <input
              type="checkbox"
              id="ativar-geolocalizacao"
              checked={geolocationSetting}
              onChange={handleGeolocationChange}
            />
            Onde usar Veloe perto de mim (até 20 km)
          </LabelGeolocalizacao>
        </Settings>
      );
    }
    return null;
  };

  const FilterButtonList = useMemo(() => {
    return categories.map(item => (
      <Button
        disabled={idle}
        onClick={event => toggleFilter(event, item.category)}
        key={item.category}
        className={item.active ? "active" : ""}
      >
        {item.displayName}
      </Button>
    ));
  }, [categories, idle, toggleFilter]);

  return (
    <div>
      <TextWrap>
        <Title>{mapTitle}</Title>
        <Sorriso />
        <Text>{mapDescriptions}</Text>
      </TextWrap>
      <Wrapper>
        {showLocationAlert && (
          <LocationAlert onClick={() => setShowLocationAlert(false)}>
            <AlertWrapper>
              <div>
                <Icon name="Atention" />
                <MessageLocationAlert>
                  Pra traçar a rota até o local de destino, ative sua
                  localização do navegador
                </MessageLocationAlert>
              </div>
              <AlertLoadingBar />
            </AlertWrapper>
          </LocationAlert>
        )}
        <GoogleMaps
          latlong={latlong}
          results={queryResults}
          onReady={setMapReady}
          onSelectMarker={onSelect}
          ref={mapRef}
        />
        <Control>
          <Form disabled={idle} className="Controls" onSubmit={onSubmit}>
            <InputWrapper>
              <Query
                disabled={idle}
                onChange={e => setQueryTerm(e.target.value)}
                onFocus={showHistory}
                type="text"
                name="query"
                value={queryTerm}
                placeholder="Digite onde quer usar Veloe"
                autoComplete="off"
              />
              <InputClear type="button" onClick={() => setQueryTerm("")}>
                <img src="/parcerias/assets/fechar.svg" alt="buscar" />
              </InputClear>
              <InputButton>
                <img src="/parcerias/assets/busca.svg" alt="buscar" />
              </InputButton>
            </InputWrapper>
            {historicoDeBusca(showSearchHistory, searchHistory)}
            <Filters>{FilterButtonList}</Filters>
          </Form>

          {isMobileDevice && latlong && (
            <WrapperSettings>
              <SmallSettings>
                <CenterLocationButton />
                <SettingsButton onClick={handleShowSettingsChange}>
                  <Icon name="Settings" />
                </SettingsButton>
              </SmallSettings>
              {showSettings && <SettingsBox />}
            </WrapperSettings>
          )}

          {idle && mapReady && (
            <Results>
              <Result>
                <LoadingWrapper>
                  <LoadingSpinner />
                </LoadingWrapper>
              </Result>
            </Results>
          )}
          <RenderResults list={queryResults} onSelect={onSelect} />
        </Control>
        {!isMobileDevice && (
          <DesktopSettingsWrapper>
            <SettingsBox />
            <CenterLocationButton />
          </DesktopSettingsWrapper>
        )}
      </Wrapper>
    </div>
  );
}
